import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { ROUTES } from "config/navConfig";

const SPV_STATUS_CONFIG = {
  active:          { label: "Active — Asset Management", color: "var(--color-success)",   bg: "rgba(5,150,105,0.1)",  icon: "BarChart3"  },
  fundraising:     { label: "Fundraising",               color: "var(--color-primary)",   bg: "rgba(59,76,76,0.1)",   icon: "TrendingUp" },
  "pre-formation": { label: "Pre-Formation",             color: "#d97706",                bg: "rgba(217,119,6,0.1)",  icon: "Clock"      },
  open:            { label: "Open – Fundraising",        color: "var(--color-success)",   bg: "rgba(5,150,105,0.1)",  icon: "TrendingUp" },
  closing:         { label: "Closing",                   color: "#d97706",                bg: "rgba(217,119,6,0.1)",  icon: "Clock"      },
  closed:          { label: "Closed",                    color: "var(--color-primary)",   bg: "rgba(59,76,76,0.1)",   icon: "Lock"       },
  "asset-management": { label: "Asset Management",       color: "var(--color-secondary)", bg: "rgba(74,107,138,0.1)", icon: "BarChart3"  },
};

export default function SPVHeader({ spv, onExport, onShare, isCoInvestor, contextEntity, fundId, fundShortName }) {
  const navigate = useNavigate();
  const statusCfg = SPV_STATUS_CONFIG?.[spv?.status] || SPV_STATUS_CONFIG?.open;

  return (
    <div className="bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4 md:py-5">
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
        {/* Back + breadcrumb */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            onClick={() => navigate(ROUTES.dashboard)}
            className="flex items-center justify-center w-8 h-8 rounded-md border border-border hover:bg-muted transition-all duration-200 flex-shrink-0"
            aria-label="Back to portfolio dashboard"
          >
            <Icon name="ArrowLeft" size={16} color="var(--color-foreground)" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {fundId && !isCoInvestor ? (
                <>
                  <button
                    onClick={() => navigate(ROUTES.funds)}
                    className="font-caption text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    Funds
                  </button>
                  <Icon name="ChevronRight" size={12} color="var(--color-muted-foreground)" />
                  <button
                    onClick={() => navigate(ROUTES.fundDetail.replace(':id', fundId))}
                    className="font-caption text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    {fundShortName ?? fundId}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => navigate(ROUTES.dashboard)}
                  className="font-caption text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  Portfolio
                </button>
              )}
              <Icon name="ChevronRight" size={12} color="var(--color-muted-foreground)" />
              <button
                onClick={() => navigate(ROUTES.spvDeals)}
                className="font-caption text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                SPVs &amp; Deals
              </button>
              <Icon name="ChevronRight" size={12} color="var(--color-muted-foreground)" />
              <span className="font-caption text-xs text-foreground font-medium truncate">{spv?.name}</span>
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground leading-tight">
                {spv?.name}
              </h1>
              <span
                className="flex items-center gap-1 font-caption text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0"
                style={{ background: statusCfg?.bg, color: statusCfg?.color }}
              >
                <Icon name={statusCfg?.icon} size={11} />
                {statusCfg?.label}
              </span>
            </div>
            <p className="font-caption text-xs text-muted-foreground mt-0.5">
              {spv?.jurisdiction} · {spv?.vehicleType} · Est. {spv?.established}
            </p>
            {isCoInvestor && contextEntity && (
              <p className="font-caption text-xs mt-1 flex items-center gap-1" style={{ color: '#0f766e' }}>
                <Icon name="Eye" size={11} color="#0f766e" />
                Viewing co-investment position on behalf of <span className="font-semibold ml-1">{contextEntity}</span>
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          <Button variant="outline" size="sm" iconName="Share2" iconPosition="left" iconSize={14} onClick={onShare}>
            Share
          </Button>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left" iconSize={14} onClick={onExport}>
            Export Report
          </Button>
          {!isCoInvestor && (
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              iconSize={14}
              onClick={() => navigate(ROUTES.spvCreation)}
            >
              New SPV
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}